/**
 * Approvals queue and My drafts (redesign 2026-10-19): a summary row that also
 * filters, then one card per document with its type color, how long it has
 * been waiting, tags and the next action.
 */
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { api, type ApprovalItem, type DocView } from "../lib/api";
import { useStore } from "../lib/store";
import { AREA_LABELS, TYPE_LABELS } from "../lib/brand";
import { fmtDT } from "../lib/format";
import { parseTs } from "../../../shared/time";
import { Avatar, Co, Empty, Loading, TypeBadge } from "../components/bits";
import { FrameworkTags } from "../components/FrameworkPicker";
import { SeeHow } from "../components/SeeHow";

/** Whole days since a timestamp. */
function daysSince(ts: string | null | undefined): number {
  const t = parseTs(ts ?? null)?.getTime();
  return t ? Math.max(0, Math.floor((Date.now() - t) / 864e5)) : 0;
}
function Age({ days, label }: { days: number; label: string }) {
  const cls = days >= 7 ? "bad" : days >= 3 ? "warn" : "ok";
  return <span className={`q-age ${cls}`}>{days === 0 ? `${label} today` : `${label} ${days} day${days === 1 ? "" : "s"}`}</span>;
}

interface Row { d: DocView; status: ReactNode; action: string; primary: boolean; note?: ReactNode }

function QueueCards({ rows, empty, selectable, selected, onToggle }: {
  rows: Row[]; empty: [string, string];
  /** My drafts: which cards can be ticked for "Submit selected", and the ticks. */
  selectable?: (d: DocView) => boolean; selected?: Set<string>; onToggle?: (id: string) => void;
}) {
  const nav = useNavigate();
  if (!rows.length) return <div className="card"><Empty title={empty[0]}>{empty[1]}</Empty></div>;
  return (
    <div className="q-list">
      {rows.map(({ d, status, action, primary, note }) => {
        const p = d.pending!;
        const go = () => nav(`/doc/${d.id}`);
        return (
          <article key={d.id} className={`card q-card ${selectable ? "q-sel" : ""} ${selected?.has(d.id) ? "on" : ""}`} tabIndex={0} onClick={go} onKeyDown={(e) => { if (e.key === "Enter") go(); }}>
            {selectable && (
              <span className="q-check" onClick={(e) => e.stopPropagation()}>
                {selectable(d) && <input type="checkbox" aria-label={`Select ${d.title}`} checked={!!selected?.has(d.id)} onChange={() => onToggle?.(d.id)} />}
              </span>
            )}
            <TypeBadge type={d.type} />
            <div className="q-main">
              <div className="q-title">{d.title}</div>
              <div className="q-sub">
                <span className="docno">{d.base_number}<span className="pend"> · v{p.proposed_version}</span></span>
                <Co code={d.company} />
                <span>{TYPE_LABELS[d.type as keyof typeof TYPE_LABELS] ?? d.type} · {AREA_LABELS[d.area as keyof typeof AREA_LABELS] ?? d.area}</span>
                <FrameworkTags tags={d.frameworks} max={3} />
              </div>
              {note ?? (d.description ? <div className="q-desc">{d.description}</div> : null)}
            </div>
            <div className="q-meta">
              <span className="q-who"><Avatar id={p.by.id} name={p.by.name} size={22} />{p.by.name}</span>
              <span className="q-when" title={fmtDT(p.submitted_at ?? d.updated_at)}>
                <Age days={daysSince(p.submitted_at ?? d.updated_at)} label={p.state === "in_review" ? "Waiting" : "Updated"} />
              </span>
            </div>
            <div className="q-act">
              {status}
              <button className={`btn btn-sm ${primary ? "btn-accent" : "btn-ghost"}`} onClick={(e) => { e.stopPropagation(); go(); }}>{action}</button>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function Summary({ items }: { items: { key: string; label: string; value: ReactNode; color: string; on?: boolean; onClick?: () => void }[] }) {
  return (
    <div className="kpis q-kpis" style={{ gridTemplateColumns: `repeat(${items.length},minmax(0,1fr))` }}>
      {items.map((k) => (
        <button key={k.key} type="button" className={`kc ${k.on ? "on" : ""}`} style={{ ["--kc" as string]: k.color, cursor: k.onClick ? "pointer" : "default" }}
          onClick={k.onClick} aria-pressed={k.onClick ? !!k.on : undefined}>
          <span className="v">{k.value}</span><span className="l">{k.label}</span>
        </button>
      ))}
    </div>
  );
}

export function ApprovalsPage() {
  const { me, ws, docs, p, reload, toast } = useStore();
  const [items, setItems] = useState<ApprovalItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<"" | "others" | "mine">("");
  // Approve selected (admins, 2026-10-20): each is a normal, individually recorded
  // approval with the same note and reviewer statement; self-approvals are labelled.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [note, setNote] = useState("");
  const [attest, setAttest] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  useEffect(() => { api.approvals().then((r) => setItems(r.items), (e: Error) => setErr(e.message)); }, [docs]);
  if (err) return <div className="card"><Empty title="Approvals didn't load">{err}</Empty></div>;
  if (!items) return <Loading />;
  const inWs = items.filter((i) => i.document.company === "CPY" || ws === "ALL" || i.document.company === ws);
  const own = (i: ApprovalItem) => i.document.pending!.by.id === me.user.id;
  const others = inWs.filter((i) => !own(i)), mine = inWs.filter(own);
  const oldest = inWs.reduce((m, i) => Math.max(m, daysSince(i.document.pending!.submitted_at ?? i.document.updated_at)), 0);
  const shown = (filter === "others" ? others : filter === "mine" ? mine : inWs)
    .slice().sort((a, b) => Number(own(a)) - Number(own(b)) || String(a.document.pending!.submitted_at ?? "").localeCompare(String(b.document.pending!.submitted_at ?? "")));
  const rows: Row[] = shown.map((i) => {
    const o = own(i);
    // ADR-0019: a review step first, or several approvers.
    const need = i.approvals?.need ?? 1, have = i.approvals?.have ?? 0;
    const status = i.stage === "review"
      ? (i.can_review ? <span className="pill info">Needs your review</span> : <span className="pill draft">Waiting for a review</span>)
      : o
        ? (i.can_approve ? <span className="pill rev">Yours · self-approval allowed</span> : <span className="pill draft">Yours · needs another approver</span>)
        : i.blocked_reason === "already_approved"
          ? <span className="pill draft">You approved · {have} of {need}</span>
          : <span className="pill rev">Awaiting you{need > 1 ? ` · ${have} of ${need}` : ""}</span>;
    const last = i.document.pending!.last_comment;
    return { d: i.document, status, action: i.can_review ? "Review" : i.can_approve ? (o ? "Review & self-approve" : "Review & approve") : "Open", primary: !!(i.can_approve || i.can_review),
      note: last ? <div className="q-desc">Previous change request: “{last}”</div> : undefined };
  });
  const toggle = (f: "others" | "mine") => setFilter((x) => (x === f ? "" : f));
  const bulkOk = p.anyAdmin;
  const approvable = new Set(shown.filter((i) => i.can_approve).map((i) => i.document.id));
  const picked = shown.filter((i) => i.can_approve && selected.has(i.document.id));
  const allOn = approvable.size > 0 && picked.length === approvable.size;
  const flip = (id: string) => setSelected((s0) => { const s1 = new Set(s0); if (s1.has(id)) s1.delete(id); else s1.add(id); return s1; });
  const approveSelected = async () => {
    let ok = 0; const failed: string[] = [];
    for (const [i, it] of picked.entries()) {
      setProgress(`Approving ${i + 1} of ${picked.length}…`);
      try { await api.approve(it.document.pending!.version_id, note.trim(), attest); ok++; }
      catch (e) { failed.push(`${it.document.base_number}: ${(e as Error).message}`); }
    }
    setProgress(null); setSelected(new Set()); setAttest(false);
    await reload();
    toast(`Approved and published ${ok}${failed.length ? `; ${failed.length} didn't approve (${failed[0]})` : ""}.`);
  };
  return (
    <div className="view">
      <div className="hgroup"><div>
        <div className="eyebrow">Separation of duties</div>
        <h1 className="title">Waiting for <em>approval</em></h1>
        <div className="lede">Approve or request changes. Where a company requires a second approver, you can't approve your own submissions; where you're its only approver, self-approval is allowed and labeled. Where a company rule asks for a review or more than one approver, each item says what it's waiting for.</div>
        <div className="lede-help"><SeeHow video="approve" /></div>
      </div></div>
      <Summary items={[
        { key: "others", label: "Waiting on you", value: others.length, color: "#E0A31A", on: filter === "others", onClick: () => toggle("others") },
        { key: "mine", label: "Your own submissions", value: mine.length, color: "#7C5CC4", on: filter === "mine", onClick: () => toggle("mine") },
        { key: "oldest", label: "Oldest waiting", value: inWs.length ? `${oldest} day${oldest === 1 ? "" : "s"}` : "-", color: oldest >= 7 ? "#AE2A20" : oldest >= 3 ? "#DC6D27" : "#2F7A52" },
      ]} />
      {bulkOk && approvable.size > 0 && (
        <div className="q-bulk q-bulk-approve">
          <label><input type="checkbox" checked={allOn} disabled={!!progress}
            onChange={() => setSelected(allOn ? new Set() : new Set(approvable))} /> Select all {approvable.size} you can approve</label>
          <input className="inp inp-sm" style={{ flex: "1 1 260px" }} value={note} maxLength={500} disabled={!!progress}
            onChange={(e) => setNote(e.target.value)} placeholder="Approval note for each, e.g. Reviewed in the IT Glue move; approved as R1" aria-label="Approval note" />
          <label className="att"><input type="checkbox" checked={attest} disabled={!!progress} onChange={(e) => setAttest(e.target.checked)} /> I reviewed each selected document and it's ready to publish</label>
          {progress && <span className="spin">{progress}</span>}
          <button className={`btn btn-accent btn-sm ${progress ? "busy" : ""}`} disabled={!picked.length || !note.trim() || !attest || !!progress} onClick={() => void approveSelected()}>
            Approve {picked.length || ""} selected
          </button>
        </div>
      )}
      <QueueCards rows={rows} empty={filter ? ["Nothing here", "No documents match this filter."] : ["All clear", "Nothing is waiting for approval."]}
        selectable={bulkOk && approvable.size ? (d) => approvable.has(d.id) : undefined} selected={selected} onToggle={flip} />
    </div>
  );
}

export function DraftsPage() {
  const { me, docs, reload, toast } = useStore();
  const [filter, setFilter] = useState<"" | "draft" | "in_review" | "changes_requested" | "hold">("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<string | null>(null);
  if (!docs) return <Loading />;
  const mine = docs.filter((d) => d.pending?.by.id === me.user.id);
  const count = (s: string) => mine.filter((d) => d.pending!.state === s).length;
  const order: Record<string, number> = { changes_requested: 0, draft: 1, in_review: 2 };
  const shown = (filter === "hold" ? mine.filter((d) => d.pending!.on_hold) : filter ? mine.filter((d) => d.pending!.state === filter && !d.pending!.on_hold) : mine)
    .slice().sort((a, b) => (order[a.pending!.state] ?? 3) - (order[b.pending!.state] ?? 3));
  const rows: Row[] = shown.map((d) => {
    const st = d.pending!.state;
    return {
      d,
      status: d.pending!.on_hold ? <span className="pill hold">On hold</span>
        : st === "in_review" ? <span className="pill rev">In review</span>
        : st === "changes_requested" ? <span className="pill chg">Changes requested</span>
        : <span className="pill draft">Draft</span>,
      action: st === "in_review" ? "Open" : st === "changes_requested" ? "Fix and resubmit" : "Open draft",
      primary: st !== "in_review",
      note: st === "changes_requested" && d.pending!.last_comment ? <div className="q-desc chg">Approver: “{d.pending!.last_comment}”</div>
        : d.pending!.last_note ? <div className="q-desc q-note">Note from {d.pending!.last_note.by}: “{d.pending!.last_note.body}”</div> : undefined,
    };
  });
  const toggle = (f: "draft" | "in_review" | "changes_requested" | "hold") => setFilter((x) => (x === f ? "" : f));
  // "Submit selected" (2026-10-20): drafts only; each is submitted exactly as the
  // document page would, quietly, then approvers get one summary email.
  const canPick = (d: DocView) => d.pending?.state === "draft" && !d.pending.on_hold;
  const pickable = shown.filter(canPick);
  const picked = pickable.filter((d) => selected.has(d.id));
  const flip = (id: string) => setSelected((s0) => { const s1 = new Set(s0); if (s1.has(id)) s1.delete(id); else s1.add(id); return s1; });
  const allOn = pickable.length > 0 && picked.length === pickable.length;
  const submitSelected = async () => {
    const done: string[] = []; const failed: string[] = [];
    for (const [i, d] of picked.entries()) {
      setProgress(`Submitting ${i + 1} of ${picked.length}…`);
      try { await api.submit(d.pending!.version_id, undefined, true); done.push(d.pending!.version_id); }
      catch (e) { failed.push(`${d.base_number}: ${(e as Error).message}`); }
    }
    if (done.length) await api.submittedSummary(done).catch(() => null);
    setProgress(null); setSelected(new Set());
    await reload();
    toast(`Submitted ${done.length} for approval${failed.length ? `; ${failed.length} didn't submit (${failed[0]})` : ""}. PDF copies are being made now.`);
  };
  return (
    <div className="view">
      <div className="hgroup"><div>
        <div className="eyebrow">Your work in progress</div>
        <h1 className="title">My <em>drafts</em> &amp; submissions</h1>
        <div className="lede">Changes requested come first. Nothing here is visible to readers until it's approved.</div>
      </div></div>
      <Summary items={[
        { key: "chg", label: "Changes requested", value: count("changes_requested"), color: "#AE2A20", on: filter === "changes_requested", onClick: () => toggle("changes_requested") },
        { key: "draft", label: "Drafts", value: mine.filter((d) => d.pending!.state === "draft" && !d.pending!.on_hold).length, color: "#6F7681", on: filter === "draft", onClick: () => toggle("draft") },
        { key: "hold", label: "On hold", value: mine.filter((d) => d.pending!.on_hold).length, color: "#7C5CC4", on: filter === "hold", onClick: () => toggle("hold") },
        { key: "rev", label: "In review", value: count("in_review"), color: "#E0A31A", on: filter === "in_review", onClick: () => toggle("in_review") },
      ]} />
      {pickable.length > 0 && (
        <div className="q-bulk">
          <label><input type="checkbox" checked={allOn} disabled={!!progress}
            onChange={() => setSelected(allOn ? new Set() : new Set(pickable.map((d) => d.id)))} /> Select all {pickable.length} draft{pickable.length === 1 ? "" : "s"}</label>
          <span className="help">Tick the drafts you've reviewed. Each goes to the approvers as if you'd clicked Submit on it; they get one email listing them all.</span>
          <span style={{ flex: 1 }} />
          {progress && <span className="spin">{progress}</span>}
          <button className={`btn btn-accent btn-sm ${progress ? "busy" : ""}`} disabled={!picked.length || !!progress} onClick={() => void submitSelected()}>
            Submit {picked.length || ""} for approval
          </button>
        </div>
      )}
      <QueueCards rows={rows} empty={filter ? ["Nothing here", "No documents match this filter."] : ["No drafts", "Start one with New document."]}
        selectable={pickable.length ? canPick : undefined} selected={selected} onToggle={flip} />
    </div>
  );
}
